import Image from 'next/image'
import { type ElementType, type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { type GkAirRecommendedProductDataWeb } from '@redteclab/api/clients/crosssellers-proxy-service'

import { Skeleton } from 'base-ui'

import {
  CSW_INTERACTED_KEY,
  useSetSessionStorageValuesForOptimizelyTracking,
} from '../../../experiments/hooks'
import { useExperimentalAddStaticFileHost } from '../../../experiments/new-cdn-experiment/useExperimentalAddStaticFileHost'
import { useGlobalConfigContext } from '../../../global-config'
import {
  IMAGE_PRODUCT_FALLBACK_SRC,
  useCdnHostedImageLoader,
} from '../../../image'
import { NextLink } from '../../../next-link'
import { urlGetRelativeUrl } from '../../../url-handling'
import { gkAirAccessorGetLocalizedProductImageUrl } from '../../model/gkAirAccessorGetLocalizedProductImageUrl'
import { gkAirAccessorGetLocalizedProductName } from '../../model/gkAirAccessorGetLocalizedProductName'
import { gkAirAccessorGetLocalizedProductUrl } from '../../model/gkAirAccessorGetLocalizedProductUrl'
import { gkAirAccessorGetSponsoredProductLink } from '../../model/gkAirAccessorGetSponsoredProductLink'
import { useGkAirAreaContext } from '../GkAirAreaProvider'

import { GkAirProductImageDiscount } from './GkAirProductImageDiscount'

type GkAirProductImageProps = {
  product?: GkAirRecommendedProductDataWeb
  slots?: {
    decorator?: ElementType
  }
}

const MAX_IMAGE_SIZE = 160

export const GkAirProductImage: FC<GkAirProductImageProps> = ({
  product,
  slots,
}) => {
  const loader = useCdnHostedImageLoader()
  const globalConfig = useGlobalConfigContext()
  const { locale } = globalConfig
  const { addStaticCdnHostToUrl } = useExperimentalAddStaticFileHost()
  const trackClick =
    useSetSessionStorageValuesForOptimizelyTracking(CSW_INTERACTED_KEY)
  const { metadata } = useGkAirAreaContext()

  if (!product) {
    return (
      <Skeleton className="px-7 py-3.5 tablet:p-3.5" variant="rectangular">
        <div className="aspect-square w-full" />
      </Skeleton>
    )
  }

  const Decorator = slots?.decorator ?? GkAirProductImageDiscount
  const sponsoredProductLink = gkAirAccessorGetSponsoredProductLink({
    metadata,
    product,
  })
  const productName = gkAirAccessorGetLocalizedProductName({
    locale,
    product,
  })

  return (
    <div className="relative rounded-t-2xl px-7 py-3.5 before:absolute before:inset-0 before:bg-gradient-radial before:from-transparent before:via-transparent before:to-[var(--background-light-primary-medium)] tablet:p-3.5">
      {sponsoredProductLink ? (
        <small className="absolute left-1/2 top-0 -translate-x-1/2 text-[#949494]">
          <FormattedMessage id="general.label.sponsored" />
        </small>
      ) : null}
      <NextLink
        aria-label={productName}
        className="flex aspect-square max-h-[130px] w-full items-center justify-center rounded-t-2xl before:absolute before:inset-0 before:z-10"
        data-qa-id={`form-product-slider-${product.UID}-image-button`}
        href={
          sponsoredProductLink ??
          urlGetRelativeUrl(
            gkAirAccessorGetLocalizedProductUrl({
              locale,
              product,
            }),
          )
        }
        onClick={(): void => {
          trackClick()
        }}
        rel={sponsoredProductLink ? 'nofollow noopener' : undefined}
      >
        <Image
          alt=""
          className="max-h-full w-auto max-w-full"
          data-analytics="item-image"
          height={MAX_IMAGE_SIZE}
          loader={loader}
          src={
            gkAirAccessorGetLocalizedProductImageUrl({
              locale,
              product,
            }) || addStaticCdnHostToUrl(IMAGE_PRODUCT_FALLBACK_SRC)
          }
          width={MAX_IMAGE_SIZE}
        />
      </NextLink>
      <Decorator product={product} />
    </div>
  )
}
